import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, MapPin, Navigation } from "lucide-react";
import { api, INSTAGRAM_URL } from "../lib/api";
import { useSeo } from "../lib/hooks";

const STATUS = {
  confirme: { label: "Confirmé", cls: "border-[#C9A45C] text-[#0B0B0B] bg-[#C9A45C]" },
  a_confirmer: { label: "À confirmer", cls: "border-dashed border-[#E5C178]/70 text-[#E5C178]" },
  annule: { label: "Annulé", cls: "border-red-400/50 text-red-300" },
};
const shift = (iso, days) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
const dayNum = (iso) => new Date(`${iso}T12:00:00`).getDate();
const monthShort = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { month: "short" });
const rangeLabel = (a, b) => `du ${dayNum(a)} ${monthShort(a)} au ${dayNum(b)} ${monthShort(b)} ${b.slice(0, 4)}`;
const mapsUrl = (m) => m.maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${m.location || "Marché"} ${m.city}`)}`;
const fmtTime = (t) => t.replace(/^0/, "").replace(":00", "h").replace(":", "h");
const hours = (m) => [m.start_time, m.end_time].filter(Boolean).map(fmtTime).join(" – ");

const Badge = ({ status, testid }) => (
  <span className={`inline-block border px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase ${STATUS[status]?.cls}`} data-testid={testid}>{STATUS[status]?.label}</span>
);

const MarketCard = ({ m, date }) => {
  const off = m.status === "annule";
  return (
    <div className={`border p-4 lg:p-3 bg-[#0B0B0B] ${off ? "border-red-400/25" : "border-[#C9A45C]/25"}`} data-testid={`market-card-${date}-${m.id}`}>
      <Badge status={m.status} testid={`market-status-${date}-${m.id}`} />
      <p className={`font-display text-xl lg:text-lg mt-3 leading-tight hyphens-auto ${off ? "line-through text-[#F8F6F1]/40" : ""}`}>{m.city}</p>
      {m.name && m.name !== `Marché de ${m.city}` && <p className="text-[11px] text-[#F8F6F1]/45 mt-1 break-words">{m.name}</p>}
      {hours(m) && <p className="mt-3 flex items-center gap-2 text-sm text-[#F8F6F1]/75 whitespace-nowrap"><Clock className="h-3.5 w-3.5 text-[#C9A45C] shrink-0" />{hours(m)}</p>}
      {m.location && <p className="mt-1.5 flex items-start gap-2 text-xs text-[#F8F6F1]/60 break-words"><MapPin className="h-3.5 w-3.5 text-[#C9A45C] shrink-0 mt-0.5" />{m.location}</p>}
      {m.note && <p className="mt-2 text-xs italic text-[#F8F6F1]/50">{m.note}</p>}
      {!off && (
        <a href={mapsUrl(m)} target="_blank" rel="noopener noreferrer" className="mt-4 flex items-center justify-center gap-2 border border-[#C9A45C]/50 py-2.5 px-1 text-[10px] lg:text-[9px] tracking-[0.15em] uppercase whitespace-nowrap text-[#C9A45C] hover:bg-[#C9A45C] hover:text-[#0B0B0B] transition-colors" data-testid={`market-maps-${date}-${m.id}`}>
          <Navigation className="h-3.5 w-3.5" />Google Maps
        </a>
      )}
    </div>
  );
};

const DayColumn = ({ d, today }) => {
  const isToday = d.date === today;
  return (
    <section className={`border p-4 lg:p-3 flex flex-col gap-3 min-w-0 ${isToday ? "border-[#C9A45C] bg-[#C9A45C]/5" : "border-[#C9A45C]/15 bg-[#101010]"} ${d.date < today ? "opacity-50" : ""}`} data-testid={`market-day-${d.weekday}`}>
      <header className="flex lg:flex-col items-baseline lg:items-start justify-between gap-2 border-b border-[#C9A45C]/15 pb-3">
        <p className="eyebrow">{d.label}{isToday && <span className="ml-2 text-[#E5C178]">· Aujourd'hui</span>}</p>
        <p className="font-display text-3xl text-[#F8F6F1]">{dayNum(d.date)} <span className="text-sm text-[#F8F6F1]/45">{monthShort(d.date)}</span></p>
      </header>
      {d.markets.length ? d.markets.map((m) => <MarketCard key={m.id} m={m} date={d.date} />)
        : <p className="text-xs text-[#F8F6F1]/40 py-4" data-testid={`market-day-empty-${d.weekday}`}>Aucun marché prévu</p>}
    </section>
  );
};

export default function Markets() {
  useSeo({ title: "Nos marchés de la semaine", description: "Retrouvez NasDeal sur les marchés de la Meuse : Commercy, Bar-le-Duc, Vigneulles, Ligny-en-Barrois, Saint-Mihiel." });
  const [week, setWeek] = useState(null);
  const [start, setStart] = useState(null);
  const [first, setFirst] = useState(null);
  useEffect(() => {
    api.get("/markets/week", { params: start ? { start } : {} }).then((r) => { setWeek(r.data); if (!first) setFirst(r.data.start); }).catch(() => setWeek({ days: [] }));
  }, [start]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20" data-testid="markets-page">
      <div className="flex flex-col sm:flex-row sm:items-end gap-8 justify-between">
        <div className="flex items-center gap-6">
          <span className="block h-20 w-20 sm:h-24 sm:w-24 shrink-0 overflow-hidden bg-black border border-[#C9A45C]/30"><img src="/logo-nasdeal.jpg" alt="Logo NasDeal" className="h-full w-full object-cover scale-[1.3]" /></span>
          <div>
            <p className="eyebrow mb-3">Rencontrons-nous</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl">Nos marchés</h1>
          </div>
        </div>
        {week?.start && (
          <div className="flex items-center gap-2" data-testid="markets-week-nav">
            <button onClick={() => setStart(shift(week.start, -7))} disabled={week.start <= first} className="h-11 w-11 border border-[#C9A45C]/40 flex items-center justify-center text-[#C9A45C] disabled:opacity-25" aria-label="Semaine précédente" data-testid="markets-prev-week"><ChevronLeft className="h-4 w-4" /></button>
            <p className="text-xs tracking-[0.15em] uppercase text-[#F8F6F1]/70 min-w-[190px] text-center" data-testid="markets-week-range">{rangeLabel(week.start, week.end)}</p>
            <button onClick={() => setStart(shift(week.start, 7))} className="h-11 w-11 border border-[#C9A45C]/40 flex items-center justify-center text-[#C9A45C]" aria-label="Semaine suivante" data-testid="markets-next-week"><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>
      <p className="mt-6 text-sm text-[#F8F6F1]/60 max-w-2xl">Venez sentir nos parfums en personne. En cas d'imprévu, les changements sont indiqués ici et sur notre <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-[#C9A45C] underline">Instagram</a>.</p>
      <div className="mt-6 flex flex-wrap gap-3" data-testid="markets-legend">{Object.keys(STATUS).map((s) => <Badge key={s} status={s} />)}</div>
      <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-7" data-testid="markets-calendar">
        {(week?.days || []).map((d) => <DayColumn key={d.date} d={d} today={week.today} />)}
      </div>
    </div>
  );
}
